import { apiRequest } from '@/lib/api-client';
import type { Session, SessionOrg } from './auth-store';

export interface LoginResponseBody {
  user: Session['user'];
  activeOrg: SessionOrg;
  accessToken: string;
  accessTokenExpiresAt: number;
}

export interface RegisterInput {
  email: string;
  password: string;
  name?: string;
  orgName?: string;
}

export interface LoginInput {
  email: string;
  password: string;
}

export function register(input: RegisterInput): Promise<LoginResponseBody> {
  return apiRequest<LoginResponseBody>('/v1/auth/register', {
    method: 'POST',
    body: input,
    skipAuth: true,
    skipRefresh: true,
  });
}

export function login(input: LoginInput): Promise<LoginResponseBody> {
  return apiRequest<LoginResponseBody>('/v1/auth/login', {
    method: 'POST',
    body: input,
    skipAuth: true,
    skipRefresh: true,
  });
}

export function logout(): Promise<{ ok: boolean }> {
  return apiRequest('/v1/auth/logout', {
    method: 'POST',
    skipAuth: true,
    skipRefresh: true,
  });
}

export function refresh(): Promise<LoginResponseBody> {
  return apiRequest<LoginResponseBody>('/v1/auth/refresh', {
    method: 'POST',
    skipAuth: true,
    skipRefresh: true,
  });
}

export interface MeResponse {
  user: Session['user'];
  activeOrg: SessionOrg;
  memberships: Array<{
    org: { id: string; slug: string; name: string };
    role: 'owner' | 'admin' | 'member';
  }>;
}

export function fetchMe(): Promise<MeResponse> {
  return apiRequest<MeResponse>('/v1/me');
}

export interface OrgListResponse {
  memberships: Array<{
    org: { id: string; slug: string; name: string };
    role: 'owner' | 'admin' | 'member';
  }>;
}

export function fetchMyOrgs(): Promise<OrgListResponse> {
  return apiRequest<OrgListResponse>('/v1/me/orgs');
}

export interface SwitchOrgResponse {
  activeOrg: SessionOrg;
  accessToken: string;
  accessTokenExpiresAt: number;
}

export function switchOrg(orgId: string): Promise<SwitchOrgResponse> {
  return apiRequest<SwitchOrgResponse>('/v1/me/switch-org', {
    method: 'POST',
    body: { orgId },
  });
}

export interface InvitePreviewResponse {
  invite: {
    orgName: string;
    orgSlug: string;
    email: string;
    role: 'owner' | 'admin' | 'member';
    invitedBy: { email: string; platformRole: 'user' | 'admin' };
    expiresAt: number;
  };
}

export function fetchInvitePreview(token: string): Promise<InvitePreviewResponse> {
  return apiRequest<InvitePreviewResponse>(`/v1/invites/${encodeURIComponent(token)}`, {
    skipAuth: true,
    skipRefresh: true,
  });
}

export interface AcceptInviteResponse {
  org: { id: string; slug: string; name: string };
  role: 'owner' | 'admin' | 'member';
}

export function acceptInvite(token: string): Promise<AcceptInviteResponse> {
  return apiRequest<AcceptInviteResponse>(`/v1/invites/${encodeURIComponent(token)}/accept`, {
    method: 'POST',
  });
}
